import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useParams } from 'react-router-dom';
import { Modal, notification } from 'antd';
import { MessageOutlined, LikeFilled, DislikeFilled } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import Spinner from '../spinner/Spinner';
import EmptyComponent from '../EmptyComponent/EmptyComponent';
import { t } from '../../i18n/i18n';
import {
  createComment,
  deleteComment,
  getInvitationComments,
} from '../../API/services/screeningService';
import {
  getUserCompanyComments,
  createUserCompanyComment,
  deleteUserCompanyComment,
} from '../../API/services/companyService';
import closeIcon from '../../assets/createSearch/close-icon.svg';
import './InvitedProfileComments.scss';
import DeleteIcon from '../../assets/profile/Delete';
import Collapsed from '../Collapsed/Collapsed';
import { nanoid } from 'nanoid';
import ManualActionSelector from '../ManualActionSelector/ManualActionSelector';
import MentionTextarea from '../MentionTextarea/MentionTextarea';
import ThreadComments from '../ThreadComments/ThreadComments';
import UploadDocumentPDF from '../UploadChallengePDF/UploadChallengePDF';
import { TestFileType } from '../../constants/enums';
import FileIcon from '../../assets/testgroupABM/fileIcon';
import FileCross from '../../assets/testgroupABM/FileCross';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

const InvitedProfileComments = ({ className, isInvitedProfile }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedComment, setSelectedComment] = useState(0);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const { user, isPublicDashboard, publicDashboardGuest } = useSelector((state) => state.auth);
  const [comments, setComments] = useState([]);
  const { recruitSessionId, id, profileId } = useParams();
  const { invitedProfile, profileInvitationsAndChallenges, uniqueProfileData } = useSelector(
    (state) => state.profiles
  );
  const [otherCommentsLoading, setOtherCommentsLoading] = useState(false);

  const [manualAction, setManualAction] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [replyToThreadId, setReplyToThreadId] = useState(null);
  const [replyToComment, setReplyToComment] = useState(null);

  const [collapsedSelected, setCollapsedSelected] = useState([]);
  const [otherComments, setOtherComments] = useState([]);

  const getCommentsFn = () => {
    if (isInvitedProfile && uniqueProfileData?.data?.[0]?.recruitSessionStage?.recruitSessionId) {
      return getInvitationComments(
        profileId,
        uniqueProfileData?.data?.[0]?.recruitSessionStage?.recruitSessionId,
        {
          order: 'DESC',
          orderBy: 'DATE',
          limit: 100,
          offset: 0,
        }
      );
    }
    return getUserCompanyComments(profileId);
  };

  useEffect(() => {
    if (!invitedProfile) {
      setLoading(true);

      getCommentsFn().then((res) => {
        setComments(res?.collection || res);
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [uniqueProfileData?.data?.[0]?.invitation?.id]);

  const handleOpenModal = (threadId = null, threadChain = []) => {
    setReplyToThreadId(threadId);

    // Find the parent comment and build the full conversation chain
    if (threadId) {
      const parentComment = comments.find((c) => c.id === threadId);

      // If threadChain is provided (replying to a thread comment), use it
      // Otherwise, just use the parent comment
      if (threadChain.length > 0) {
        setReplyToComment({
          ...parentComment,
          conversationChain: [parentComment, ...threadChain],
        });
      } else {
        setReplyToComment(parentComment);
      }
    }

    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setText('');
    setManualAction(null);
    setUploadedFile(null);
    setReplyToThreadId(null);
    setReplyToComment(null);
  };

  const handlePostComment = async () => {
    if (text.length > 1000) {
      notification.open({
        message: t('NOTES_LIMIT_OF_CHARACTERS_1000'),
        type: 'error',
      });
      return null;
    }

    if (!text.trim()) {
      notification.open({
        message: t('NOTES_LIMIT_OF_CHARACTERS'),
        type: 'error',
      });
      return null;
    }

    const createCommentFn = () => {
      const commentData = {
        text,
        profileId,
        ...(manualAction && { manualAction }),
        ...(uploadedFile && { file: uploadedFile }),
        ...(replyToThreadId && { threadId: replyToThreadId }),
      };

      if (isInvitedProfile) {
        return createComment({ ...commentData, recruitSessionId });
      }
      return createUserCompanyComment(text, profileId, {
        ...(manualAction && { manualAction }),
        ...(uploadedFile && { file: uploadedFile }),
        ...(replyToThreadId && { threadId: replyToThreadId }),
      });
    };

    await createCommentFn().then((res) => {
      if (!Object.keys(res).length) {
        notification.open({
          message: t('CREATE_COMMENT_ERROR'),
          type: 'error',
        });
      } else {
        notification.open({
          message: t('CREATE_COMMENT_SUCCESS'),
          type: 'success',
        });

        const commentator = isPublicDashboard ? publicDashboardGuest : user;
        const newComment = {
          createdAt: res.createdAt,
          text,
          id: res.id,
          partnerName: `${commentator?.firstName} ${commentator?.lastName}`,
          partnerId: commentator.id,
          authorEmail: commentator.email,
          manualAction,
          file: res.file || null,
          threadId: replyToThreadId,
          threadComments: [],
        };

        if (replyToThreadId) {
          // Add to thread
          setComments((prevComments) =>
            prevComments.map((comment) => {
              if (comment.id === replyToThreadId) {
                return {
                  ...comment,
                  threadComments: [...(comment.threadComments || []), newComment],
                };
              }
              return comment;
            })
          );
        } else {
          setComments([newComment, ...comments]);
        }

        handleCloseModal();
      }
    });
  };

  const handleDeleteComment = async () => {
    const deleteCommentFn = isInvitedProfile ? deleteComment : deleteUserCompanyComment;
    await deleteCommentFn(selectedComment).then(() => {
      notification.open({
        message: t('DELETE_COMMENT_SUCCESS'),
        type: 'success',
      });

      setComments((prevComments) =>
        prevComments
          .filter((item) => item.id !== selectedComment)
          .map((comment) => ({
            ...comment,
            threadComments: comment.threadComments?.filter((tc) => tc.id !== selectedComment) || [],
          }))
      );
      setSelectedComment(0);
    });
  };

  const fetchComments = async () => {
    setOtherCommentsLoading(true);
    if (profileInvitationsAndChallenges?.invitations?.length) {
      try {
        const allComments = await Promise.all(
          profileInvitationsAndChallenges.invitations.map(async (invitation) => {
            if (
              invitation?.recruitSessionId === uniqueProfileData?.recruitSession?.id ||
              !invitation.recruitSessionId
            )
              return null;
            const comments = await getInvitationComments(
              invitation.profileId,
              invitation?.recruitSessionId,
              {
                order: 'DESC',
                orderBy: 'DATE',
                offset: 0,
                limit: 100,
              }
            );
            if (comments?.collection?.length > 0) {
              return {
                comments: comments.collection,
                screeningName: invitation.name,
              };
            }
            return null;
          })
        );

        const filteredComments = allComments.filter((comment) => comment !== null);
        setOtherComments(filteredComments);
      } catch (error) {
        console.error('Error fetching comments:', error);
      }
    }
    setOtherCommentsLoading(false);
  };

  const handleAddFile = (file, type) => {
    if (type === TestFileType.PDF) {
      setUploadedFile(file.fileObject);
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
  };

  const renderFileLink = (file) => {
    if (!file) return null;

    return (
      <div className="comment-file-container mt-2" data-testid="comment-file">
        <FileIcon className="mr-1" fill="#ff7676ff" />
        <a
          href={file.url}
          target="_blank"
          rel="noopener noreferrer"
          className="comment-file-link text-xs"
          download={file.name}
        >
          {file.name}
        </a>
      </div>
    );
  };

  const calculateManualActionCounts = () => {
    let likes = 0;
    let dislikes = 0;

    comments.forEach((comment) => {
      // Count main comment
      if (comment.manualAction === 'LIKE_IT') likes++;
      if (comment.manualAction === 'DONT_LIKE_IT') dislikes++;

      // Count thread comments
      if (comment.threadComments && comment.threadComments.length > 0) {
        comment.threadComments.forEach((threadComment) => {
          if (threadComment.manualAction === 'LIKE_IT') likes++;
          if (threadComment.manualAction === 'DONT_LIKE_IT') dislikes++;
        });
      }
    });

    return { likes, dislikes };
  };

  return (
    <div
      className={`position-relative description-container  bg-white mx-auto notes-section  ${className}`}
    >
      <Modal
        className="screening-profile-comment-modal"
        open={showModal}
        onCancel={handleCloseModal}
        closeIcon={<img src={closeIcon} alt={t('CLOSE_ALT')} />}
        footer={
          <button
            onClick={handlePostComment}
            className="font-weight-bold d-flex align-items-center border-0 outline-none px-4 py-2 text-white bg-maya-blue "
            type="button"
          >
            {t('SAVE_COMMENT_BUTTON')}
          </button>
        }
      >
        <div className="comment-modal-content">
          <h4 className="text-sm font-weight-600 mb-3">
            {replyToThreadId ? t('REPLY_TO_COMMENT') : t('ADD_COMMENT_TITLE')}
          </h4>

          {replyToComment && (
            <div className="reply-to-comment-preview mb-3 p-3 bg-light  border">
              <div className="d-flex align-items-center gap-2 mb-2">
                <span className="font-weight-600 text-xs text-muted">{t('REPLYING_TO')}:</span>
              </div>
              {replyToComment.conversationChain ? (
                <div className="conversation-chain">
                  {replyToComment.conversationChain.map((comment, index) => (
                    <div
                      key={comment.id || index}
                      className={`chain-comment ${index > 0 ? 'chain-comment-reply' : ''}`}
                    >
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <span className="font-weight-bold text-xs">
                          {comment.partnerName
                            ? comment.partnerName
                            : `${comment.authorName} ${comment.authorLastname}`}
                        </span>
                        <span className="text-xs text-muted">
                          ({format(new Date(comment.createdAt), 'dd/MM/yyyy', { locale: es })})
                        </span>
                      </div>
                      <p className="text-xs text-muted mb-2 comment-preview-text">
                        {comment.text || comment.comment}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <span className="font-weight-bold text-xs">
                      {replyToComment.partnerName
                        ? replyToComment.partnerName
                        : `${replyToComment.authorName} ${replyToComment.authorLastname}`}
                    </span>
                  </div>
                  <p className="text-xs text-muted mb-0 comment-preview-text">
                    {replyToComment.text || replyToComment.comment}
                  </p>
                </>
              )}
            </div>
          )}
          <div className="mb-3 d-flex flex-row align-items-center align-items-center w-100">
            <span className="text-xs mr-4 font-weight-400">
              {t('COMMENT_TO_LIKE_DISLIKE_CANDIDATE')}
            </span>

            <ManualActionSelector value={manualAction} onChange={setManualAction} />
          </div>

          <div>
            <MentionTextarea
              value={text}
              onChange={setText}
              placeholder={t('ADD_COMMENT_PLACEHOLDER')}
              maxLength={1000}
            />
          </div>
          <div className="mb-3">
            {!uploadedFile ? (
              <UploadDocumentPDF
                addFile={handleAddFile}
                id="comment-pdf"
                name={t('UPLOAD_PDF_DOCUMENT')}
              />
            ) : (
              <div className="uploaded-file-preview border-pill-pdf pr-2">
                <FileIcon className="mr-1" fill="#ff7676ff" />
                <span className="text-xs">{uploadedFile.name}</span>
                <button
                  type="button"
                  onClick={handleRemoveFile}
                  className="ml-2 border-0 bg-transparent cursor-pointer"
                >
                  <FileCross />
                </button>
              </div>
            )}
          </div>
        </div>
      </Modal>

      <Modal
        open={Boolean(selectedComment)}
        onOk={handleDeleteComment}
        onCancel={() => setSelectedComment(0)}
        okText={t('DELETE_COMMENT_MODAL_TEXT_CONFIRM')}
        cancelText={t('DELETE_COMMENT_MODAL_TEXT_CANCEL')}
      >
        <div className="p-3 invitation-status-modal-container">
          <span className="">{t('DELETE_COMMENT_MODAL_TEXT')}</span>
        </div>
      </Modal>

      <div className="d-flex flex-row justify-content-between">
        <div className="d-flex flex-column mb-4 px-4 pt-3">
          <span className="font-weight-600 ">{t('INVITED_PROFILE_COMMENTS_TITLE')}</span>
          <span className="text-sm invited-profile-comment-text text-xs font-weight-500">
            {t('COMMENTS_SUBTITLE')}
          </span>
        </div>
        {(() => {
          const { likes, dislikes } = calculateManualActionCounts();
          const hasActions = likes > 0 || dislikes > 0;

          return (
            hasActions && (
              <div
                className="d-flex justify-conten align-items-center px-4"
                data-testid="manual-actions-counter"
              >
                <div className="d-flex align-items-center gap-2 mr-4">
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip className="" placement="top">
                        {t('COMMENT_MANUAL_ACTION_LIKE_IT')}
                      </Tooltip>
                    }
                  >
                    <LikeFilled style={{ fontSize: '20px', color: '#67e0aeff' }} />
                  </OverlayTrigger>
                  <span className="font-weight-600 text-sm">{likes}</span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip className="" placement="top">
                        {t('COMMENT_MANUAL_ACTION_DONT_LIKE_IT')}
                      </Tooltip>
                    }
                  >
                    <DislikeFilled style={{ fontSize: '20px', color: '#ff7676ff' }} />
                  </OverlayTrigger>
                  <span className="font-weight-600 text-sm">{dislikes}</span>
                </div>
                <button
                  onClick={() => handleOpenModal()}
                  type="button"
                  className="font-weight-600 text-xs d-flex align-items-center border-0 outline-none ml-5 px-2 py-2 text-white bg-maya-blue "
                >
                  {t('ADD_PROFILE_NOTES')}
                </button>
              </div>
            )
          );
        })()}
      </div>

      {loading ? (
        <Spinner show />
      ) : (
        <div className="d-flex flex-column">
          {!comments?.length ? (
            <EmptyComponent
              className="p-4"
              title={t('PROFILE_COMMENTS_EMPTY_STATE_TITLE')}
              subtitle={
                <button
                  onClick={() => handleOpenModal()}
                  type="button"
                  className="text-xs border-blue-principal cursor-pointer rounded-0 d-flex align-items-center font-weight-700 text-blue-principal bg-white notes-empty-state-button border-button"
                >
                  {t('ADD_PROFILE_NOTES')}
                </button>
              }
              subtitleClassname="text-center"
            />
          ) : (
            <div className="px-4 d-flex flex-column gap-1">
              {comments?.map((comment, i) => (
                <div
                  key={comment?.id || i}
                  className={`border-bottom d-flex flex-column ${i !== 0 && 'mt-4'}`}
                >
                  <div className="d-flex align-items-start justify-content-between">
                    <div className="d-flex flex-column gap-2 flex-grow-1">
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <span className="font-weight-bold text-xs invited-profile-comment-title-data">
                          {comment.partnerName
                            ? comment.partnerName
                            : `${comment.authorName} ${comment.authorLastname}`}
                        </span>
                        <span className="text-xs text-muted">
                          ({format(new Date(comment.createdAt), 'dd/MM/yyyy', { locale: es })})
                        </span>
                        {comment.manualAction && (
                          <div className="d-flex align-items-center gap-3">
                            <ManualActionSelector value={comment.manualAction} readOnly />
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleOpenModal(comment.id)}
                        className="invited-profile-reply-comment-button d-flex align-items-center outline-none"
                        title={t('REPLY_TO_COMMENT')}
                      >
                        <MessageOutlined style={{ color: '#4987be', fontSize: '16px' }} />
                      </button>
                      {user?.id === comment.partnerId && (
                        <button
                          type="button"
                          onClick={() => setSelectedComment(comment?.id)}
                          className="invited-profile-delete-comment-button d-flex align-items-center outline-none"
                        >
                          <DeleteIcon fill="#4987be" />
                        </button>
                      )}
                    </div>
                  </div>

                  <p className="text-xs mt-2 invited-profile-comment-text font-weight-500">
                    {comment.text || comment.comment}
                  </p>

                  {renderFileLink(comment.file)}

                  <ThreadComments
                    threadComments={comment.threadComments}
                    onDelete={setSelectedComment}
                    currentUserId={user?.id}
                    onReply={handleOpenModal}
                    parentCommentId={comment.id}
                  />
                </div>
              ))}
            </div>
          )}

          {!isPublicDashboard &&
            [
              {
                title: t('COMMENTS_FROM_OTHER_EVALUATIONS'),
                content: (
                  <div className="d-flex flex-column gap-2 pt-2 pb-4 pr-4 ">
                    {!otherCommentsLoading ? (
                      Boolean(otherComments?.length) ? (
                        otherComments?.map((groupOfComments, index) => {
                          // Calculate likes and dislikes for this group of comments
                          const groupLikes =
                            groupOfComments?.comments?.filter((c) => c.manualAction === 'LIKE_IT')
                              .length || 0;
                          const groupDislikes =
                            groupOfComments?.comments?.filter(
                              (c) => c.manualAction === 'DONT_LIKE_IT'
                            ).length || 0;
                          const hasGroupActions = groupLikes > 0 || groupDislikes > 0;

                          return (
                            <div className="py-1" key={nanoid()}>
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="font-weight-700 text-md color-blue-principal text-underline">
                                  {groupOfComments?.screeningName}
                                </span>
                                {hasGroupActions && (
                                  <div className="d-flex align-items-center gap-3">
                                    <div className="d-flex align-items-center gap-2">
                                      <span title={t('COMMENT_MANUAL_ACTION_LIKE_IT')}>
                                        <LikeFilled
                                          style={{ fontSize: '16px', color: '#67e0aeff' }}
                                        />
                                      </span>
                                      <span className="font-weight-600 text-xs">{groupLikes}</span>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                      <span title={t('COMMENT_MANUAL_ACTION_DONT_LIKE_IT')}>
                                        <DislikeFilled
                                          style={{ fontSize: '16px', color: '#ff7676ff' }}
                                        />
                                      </span>
                                      <span className="font-weight-600 text-xs">
                                        {groupDislikes}
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div className="d-flex flex-column gap-1">
                                {groupOfComments?.comments?.map((comment) => (
                                  <div
                                    className={`border-bottom-comment d-flex flex-column gap-2 pb-1`}
                                    key={comment?.id}
                                  >
                                    <div className="d-flex justify-content-between align-items-start w-100">
                                      <div className="d-flex flex-column gap-2 flex-grow-1">
                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                          <span className="font-montserrat font-weight-bold text-sm invited-profile-comment-title-data">
                                            {comment.partnerName
                                              ? comment.partnerName
                                              : `${comment.authorName} ${comment.authorLastname}`}
                                          </span>
                                          <span className="text-xs text-muted">
                                            (
                                            {format(new Date(comment.createdAt), 'dd/MM/yyyy', {
                                              locale: es,
                                            })}
                                            )
                                          </span>
                                          {comment.manualAction && (
                                            <div className="d-flex align-items-center gap-3">
                                              <ManualActionSelector
                                                value={comment.manualAction}
                                                readOnly
                                              />
                                            </div>
                                          )}
                                        </div>
                                      </div>

                                      {(user?.id === comment.partnerId ||
                                        user?.id === comment.authorId) && (
                                        <button
                                          type="button"
                                          onClick={() => setSelectedComment(comment?.id)}
                                          className="invited-profile-delete-comment-button d-flex align-items-center outline-none"
                                        >
                                          <DeleteIcon fill="#D9D9D9" />
                                        </button>
                                      )}
                                    </div>
                                    <p className="font-montserrat text-sm mt-1 invited-profile-comment-text text-xs font-weight-500">
                                      {comment.text || comment.comment}
                                    </p>

                                    {renderFileLink(comment.file)}
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <EmptyComponent
                          className="p-4"
                          title={t('PROFILE_OTHER_COMMENTS_EMPTY_STATE_TITLE')}
                        />
                      )
                    ) : (
                      <Spinner show />
                    )}
                  </div>
                ),
                isVisible: true,
                collapsedEnabled: true,
              },
            ].map((item, index) => (
              <Collapsed
                key={item + index}
                collapsedSelected={collapsedSelected}
                setCollapsedSelected={setCollapsedSelected}
                index={index}
                options={item}
                collapsedEnabled={item.collapsedEnabled}
                containerClassname={'py-0 my-0 px-4 '}
                titleClassnames={
                  'justify-content-end align-items-center gap-1 font-weight-400 text-xs text-muted py-2'
                }
                backgroundColor={'bg-blue-light'}
                maxHeight="20rem"
                onClick={fetchComments}
              />
            ))}
        </div>
      )}
    </div>
  );
};

export default InvitedProfileComments;
